import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  acceptProcedureSetInAPI,
  ActiveProcedureSet,
  ApiDocument,
  getActiveProcedureSetFromAPI,
  getDocumentFromAPI,
  getDocumentReferencedProcedureSetsFromAPI,
  getDocumentTaskQueueFromAPI,
  getDocumentWorkflowActionsFromAPI,
  isAppendOnlyLifecyclePersistenceUnavailableError,
  isDocumentLifecycleStateChangedError,
  isDocumentPolicyAccessPersistenceUnavailableError,
  isDocumentPolicyAccessStateChangedError,
  isDocumentProcedureAccessPersistenceUnavailableError,
  isDocumentProcedureAccessStateChangedError,
  isDocumentProcedureIntegrityError,
  isSignatureCreationStateChangedError,
  isSignatureVerificationStateChangedError,
  recordAuthorityReviewInAPI,
  ProcedureSetAcceptanceEvidence,
  SignatureEvidence,
  SignatureVerificationResult,
  DocumentLifecycleStatus,
  DocumentReferencedProcedureSets,
  DocumentAuthorityWorkflowAction,
  DocumentTask,
  DocumentTaskQueue,
  DocumentWorkflowAction,
  DocumentWorkflowActionsResponse,
  signDocumentVersionInAPI,
  transitionDocumentInAPI,
  updateDocumentParticipantsInAPI,
  verifyDocumentSignaturesInAPI,
} from '../services/eCMRService';
import { EcmrParticipant, EcmrParticipantRole } from '../types/eCMR';

const transportLifecycleTaskIds = new Set([
  'carrier_acceptance_required',
  'transit_start_required',
  'delivery_confirmation_required',
]);
const activeTransportLifecycleTaskStatuses = new Set(['open', 'in_progress']);
const contractParticipantRoles = ['consignor', 'carrier'] as const;

const workflowActionIdByTargetStatus: Partial<Record<DocumentLifecycleStatus, string>> = {
  accepted_by_carrier: 'transition_to_accepted_by_carrier',
  in_transit: 'transition_to_in_transit',
  delivered: 'transition_to_delivered',
};

const workflowActionAvailabilities = new Set(['available', 'blocked_by_role', 'blocked_by_prerequisite']);
const authorityReviewActionAvailabilities = new Set(['available', 'blocked_by_role', 'blocked_by_prerequisite']);

type WorkflowActionsLoadResult = {
  actions: DocumentWorkflowActionsResponse | null;
  unavailable: boolean;
};

const workflowLoadErrorMessage = 'No se pudo cargar el flujo del documento. Reintenta o contacta soporte.';
const workflowActionErrorMessage = 'No se pudo completar la acción del flujo. Reintenta o contacta soporte.';
const lifecycleConflictMessage = 'El estado del documento cambió en otra sesión. Se ha recargado la política backend.';
const lifecycleConflictReloadErrorMessage =
  'El estado del documento cambió en otra sesión y no se pudo recargar la política backend.';
const lifecyclePersistenceUncertainMessage =
  'No se pudo confirmar la transición. Se ha recargado la política backend; comprueba el estado antes de reintentar.';
const lifecyclePersistenceUncertainReloadErrorMessage =
  'No se pudo confirmar la transición ni recargar la política backend. Reintenta la carga del documento.';
const signatureVerificationConflictMessage =
  'El documento cambió durante la verificación de firmas. Recarga la versión vigente antes de repetirla.';
const signatureCreationConflictMessage =
  'El documento cambió durante la firma. Recarga la versión vigente antes de volver a firmar.';

const documentPolicyAccessConflictMessage =
  'El documento cambio mientras se cargaba la politica backend. Recarga la version vigente antes de continuar.';
const documentPolicyAccessUnavailableMessage =
  'La politica backend esta temporalmente bloqueada porque no se pudo registrar su evidencia de acceso.';
const documentProcedureIntegrityMessage =
  'No se pueden determinar los procedimientos referenciados hasta verificar la integridad de la version vigente.';
const documentProcedureAccessConflictMessage =
  'El documento cambio mientras se resolvian sus procedimientos. Recarga la version vigente antes de continuar.';
const documentProcedureAccessUnavailableMessage =
  'Los procedimientos referenciados estan bloqueados porque no se pudo registrar su evidencia de acceso.';

const documentPolicyAccessFailureMessage = (error: unknown): string | undefined => {
  if (isDocumentPolicyAccessStateChangedError(error)) {
    return documentPolicyAccessConflictMessage;
  }
  if (isDocumentPolicyAccessPersistenceUnavailableError(error)) {
    return documentPolicyAccessUnavailableMessage;
  }
  return undefined;
};

const documentProcedureAccessFailureMessage = (error: unknown): string | undefined => {
  if (isDocumentProcedureIntegrityError(error)) {
    return documentProcedureIntegrityMessage;
  }
  if (isDocumentProcedureAccessStateChangedError(error)) {
    return documentProcedureAccessConflictMessage;
  }
  if (isDocumentProcedureAccessPersistenceUnavailableError(error)) {
    return documentProcedureAccessUnavailableMessage;
  }
  return undefined;
};

const activeTransportLifecycleTasks = (taskQueue: DocumentTaskQueue | null): DocumentTask[] =>
  (taskQueue?.tasks ?? []).filter(
    (task) => transportLifecycleTaskIds.has(task.taskId) && activeTransportLifecycleTaskStatuses.has(task.status)
  );

const hasActiveContractParticipantIdentityTask = (taskQueue: DocumentTaskQueue | null): boolean =>
  (taskQueue?.tasks ?? []).some(
    (task) =>
      task.taskId === 'contract_participant_identity_required' &&
      activeTransportLifecycleTaskStatuses.has(task.status)
  );

const hasActiveEntitledPartyAccessTask = (taskQueue: DocumentTaskQueue | null): boolean =>
  (taskQueue?.tasks ?? []).some(
    (task) =>
      task.taskId === 'entitled_party_access_required' &&
      activeTransportLifecycleTaskStatuses.has(task.status)
  );

const participantEmailForRole = (
  participants: EcmrParticipant[] | undefined,
  role: EcmrParticipantRole
): string => {
  const email = participants?.find((participant) => participant.role === role)?.email;
  return email?.trim().replace(/^mailto:/i, '') || '';
};

const upsertParticipantEmail = (
  participants: EcmrParticipant[],
  role: EcmrParticipantRole,
  email: string
): EcmrParticipant[] => {
  let updated = false;
  const nextParticipants = participants.map((participant) => {
    if (!updated && participant.role === role) {
      updated = true;
      return { ...participant, email };
    }
    return participant;
  });
  if (!updated) {
    nextParticipants.push({ role, email });
  }
  return nextParticipants;
};

const isAllowedWorkflowAction = (action: DocumentWorkflowAction): boolean =>
  workflowActionIdByTargetStatus[action.targetStatus] === action.actionId &&
  workflowActionAvailabilities.has(action.availability);

const activeWorkflowActions = (workflowActions: DocumentWorkflowActionsResponse | null): DocumentWorkflowAction[] =>
  (workflowActions?.actions ?? []).filter(isAllowedWorkflowAction);

const isAllowedAuthorityReviewAction = (action: DocumentAuthorityWorkflowAction): boolean =>
  action.actionId === 'authority_document_review' &&
  action.actionType === 'authority_review' &&
  authorityReviewActionAvailabilities.has(action.availability);

const activeAuthorityReviewActions = (workflowActions: DocumentWorkflowActionsResponse | null): DocumentAuthorityWorkflowAction[] =>
  (workflowActions?.authorityActions ?? []).filter(isAllowedAuthorityReviewAction);

const workflowActionLabel = (targetStatus: DocumentLifecycleStatus): string => {
  switch (targetStatus) {
    case 'accepted_by_carrier':
      return 'Aceptar eCMR';
    case 'in_transit':
      return 'Iniciar tránsito';
    case 'delivered':
      return 'Abrir evidencia de entrega';
    default:
      return 'Acción lifecycle';
  }
};

const countLabel = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

const hasBackendVersionReference = (record: {
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
}): boolean =>
  Boolean(record.documentVersionReferencePresent || record.documentVersionId || record.documentVersionNumber !== undefined);

const authoritativeSourceRecords = <T,>(sourceRecords?: T[], legacyRecords?: T[]): T[] => {
  if (Array.isArray(sourceRecords)) {
    return sourceRecords;
  }

  return Array.isArray(legacyRecords) ? legacyRecords : [];
};

const DocumentWorkflow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<ApiDocument | null>(null);
  const [procedureSet, setProcedureSet] = useState<ActiveProcedureSet | null>(null);
  const [referencedProcedureSets, setReferencedProcedureSets] = useState<DocumentReferencedProcedureSets | null>(null);
  const [acceptance, setAcceptance] = useState<ProcedureSetAcceptanceEvidence | null>(null);
  const [signature, setSignature] = useState<SignatureEvidence | null>(null);
  const [verification, setVerification] = useState<SignatureVerificationResult | null>(null);
  const [taskQueue, setTaskQueue] = useState<DocumentTaskQueue | null>(null);
  const [workflowActions, setWorkflowActions] = useState<DocumentWorkflowActionsResponse | null>(null);
  const [workflowActionsUnavailable, setWorkflowActionsUnavailable] = useState(false);
  const [transportUpdateMessage, setTransportUpdateMessage] = useState('');
  const [authorityReviewMessage, setAuthorityReviewMessage] = useState('');
  const [contractParticipantUpdateMessage, setContractParticipantUpdateMessage] = useState('');
  const [consignorEmail, setConsignorEmail] = useState('');
  const [carrierEmail, setCarrierEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<'accept' | 'sign' | 'verify' | 'transport' | 'authorityReview' | 'identities' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('ID de documento no proporcionado');
      setLoading(false);
      return;
    }

    const loadWorkflowData = async () => {
      setLoading(true);
      setError(null);
      setReferencedProcedureSets(null);

      try {
        const [loadedDocument, activeProcedureSet, loadedReferencedProcedureSets, loadedTaskQueue, loadedWorkflowActions] = await Promise.all([
          getDocumentFromAPI(id),
          getActiveProcedureSetFromAPI(),
          getDocumentReferencedProcedureSetsFromAPI(id),
          getDocumentTaskQueueFromAPI(id).catch((policyError) => {
            const message = documentPolicyAccessFailureMessage(policyError);
            if (message) setError(message);
            return null;
          }),
          getDocumentWorkflowActionsFromAPI(id)
            .then((actions): WorkflowActionsLoadResult => ({ actions, unavailable: false }))
            .catch((policyError): WorkflowActionsLoadResult => {
              const message = documentPolicyAccessFailureMessage(policyError);
              if (message) setError(message);
              return { actions: null, unavailable: true };
            }),
        ]);

        setDocument(loadedDocument);
        setProcedureSet(activeProcedureSet);
        setReferencedProcedureSets(loadedReferencedProcedureSets);
        setTaskQueue(loadedTaskQueue);
        setWorkflowActions(loadedWorkflowActions.actions);
        setWorkflowActionsUnavailable(loadedWorkflowActions.unavailable);
        setConsignorEmail(participantEmailForRole(loadedDocument.participants, 'consignor'));
        setCarrierEmail(participantEmailForRole(loadedDocument.participants, 'carrier'));
        setAcceptance(authoritativeSourceRecords(
          loadedDocument.procedureAcceptanceRecords,
          loadedDocument.procedureAcceptances
        )[0] ?? null);
        setSignature(authoritativeSourceRecords(
          loadedDocument.signatureEvidenceRecords,
          loadedDocument.signatureEvidence
        )[0] ?? null);
      } catch (err) {
        setReferencedProcedureSets(null);
        setError(documentProcedureAccessFailureMessage(err) ?? workflowLoadErrorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadWorkflowData();
  }, [id]);

  const documentId = document?.document_id || document?.documentId || id || '';
  const currentVersion = document?.currentVersion;
  const procedureEntries = useMemo(
    () => Object.entries(procedureSet?.procedures ?? {}),
    [procedureSet]
  );
  const lifecycleActions = activeWorkflowActions(workflowActions);
  const availableLifecycleActions = lifecycleActions.filter((action) => action.availability === 'available');
  const prerequisiteBlockedLifecycleActions = lifecycleActions.filter((action) => action.availability === 'blocked_by_prerequisite');
  const roleBlockedLifecycleActions = lifecycleActions.filter((action) => action.availability === 'blocked_by_role');
  const lifecycleActionsHaveBackendVersionReference = lifecycleActions.some(hasBackendVersionReference);
  const authorityReviewActions = activeAuthorityReviewActions(workflowActions);
  const availableAuthorityReviewActions = authorityReviewActions.filter((action) => action.availability === 'available');
  const roleBlockedAuthorityReviewActions = authorityReviewActions.filter((action) => action.availability === 'blocked_by_role');
  const prerequisiteBlockedAuthorityReviewActions = authorityReviewActions.filter(
    (action) => action.availability === 'blocked_by_prerequisite'
  );
  const authorityReviewHasBackendVersionReference = authorityReviewActions.some(hasBackendVersionReference);
  const transportLifecycleTasks = workflowActionsUnavailable ? activeTransportLifecycleTasks(taskQueue) : [];
  const carrierAcceptanceTaskCount = transportLifecycleTasks.filter((task) => task.taskId === 'carrier_acceptance_required').length;
  const transitStartTaskCount = transportLifecycleTasks.filter((task) => task.taskId === 'transit_start_required').length;
  const deliveryConfirmationTaskCount = transportLifecycleTasks.filter((task) => task.taskId === 'delivery_confirmation_required').length;
  const transportLifecycleHasBackendVersionReference = transportLifecycleTasks.some(hasBackendVersionReference);
  const contractParticipantIdentityRequired = hasActiveContractParticipantIdentityTask(taskQueue);
  const entitledPartyAccessRequired = hasActiveEntitledPartyAccessTask(taskQueue);

  const runAction = async <T,>(
    action: 'accept' | 'sign' | 'verify' | 'transport' | 'authorityReview' | 'identities',
    callback: () => Promise<T>,
    onSuccess: (result: T) => void
  ) => {
    setBusyAction(action);
    setError(null);

    try {
      const result = await callback();
      onSuccess(result);
    } catch (err) {
      const lifecycleConflict = isDocumentLifecycleStateChangedError(err);
      const lifecyclePersistenceUncertain = isAppendOnlyLifecyclePersistenceUnavailableError(err);
      const signatureVerificationConflict = isSignatureVerificationStateChangedError(err);
      const signatureCreationConflict = isSignatureCreationStateChangedError(err);
      if (action === 'sign' && signatureCreationConflict) {
        setSignature(null);
        setError(signatureCreationConflictMessage);
      } else if (action === 'verify' && signatureVerificationConflict) {
        setVerification(null);
        setError(signatureVerificationConflictMessage);
      } else if (action === 'transport' && (lifecycleConflict || lifecyclePersistenceUncertain) && documentId) {
        let reloadSucceeded = false;
        try {
          const [latestDocument, latestTaskQueue, latestWorkflowActions] = await Promise.all([
            getDocumentFromAPI(documentId),
            getDocumentTaskQueueFromAPI(documentId).catch(() => null),
            getDocumentWorkflowActionsFromAPI(documentId)
              .then((actions): WorkflowActionsLoadResult => ({ actions, unavailable: false }))
              .catch((): WorkflowActionsLoadResult => ({ actions: null, unavailable: false })),
          ]);
          setDocument(latestDocument);
          setTaskQueue(latestTaskQueue);
          setWorkflowActions(latestWorkflowActions.actions);
          setWorkflowActionsUnavailable(latestWorkflowActions.unavailable);
          reloadSucceeded = true;
        } catch (_reloadError) {
          setTaskQueue(null);
          setWorkflowActions(null);
          setWorkflowActionsUnavailable(false);
        }
        setError(
          reloadSucceeded
            ? (lifecycleConflict ? lifecycleConflictMessage : lifecyclePersistenceUncertainMessage)
            : (lifecycleConflict
              ? lifecycleConflictReloadErrorMessage
              : lifecyclePersistenceUncertainReloadErrorMessage)
        );
      } else {
        setError(workflowActionErrorMessage);
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleAcceptProcedures = () => {
    if (!documentId || !procedureSet) return;

    runAction(
      'accept',
      () => acceptProcedureSetInAPI(documentId, procedureSet.procedureSetId),
      setAcceptance
    );
  };

  const handleSignVersion = () => {
    if (!documentId || !currentVersion?.versionId) return;

    runAction(
      'sign',
      () => signDocumentVersionInAPI(
        documentId,
        currentVersion.versionId,
        'simple-electronic-attestation',
        { source: 'frontend-workflow-ui' }
      ),
      setSignature
    );
  };

  const handleVerifySignatures = () => {
    if (!documentId) return;

    runAction(
      'verify',
      () => verifyDocumentSignaturesInAPI(documentId),
      setVerification
    );
  };

  const handleContractParticipantUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!documentId || !document) return;

    const normalizedEmails = {
      consignor: consignorEmail.trim(),
      carrier: carrierEmail.trim(),
    };
    if (!normalizedEmails.consignor || !normalizedEmails.carrier) return;

    setBusyAction('identities');
    setError(null);
    setContractParticipantUpdateMessage('');
    try {
      const participants = contractParticipantRoles.reduce(
        (currentParticipants, role) => upsertParticipantEmail(
          currentParticipants,
          role,
          normalizedEmails[role]
        ),
        [...(document.participants || [])]
      );
      const updatedDocument = await updateDocumentParticipantsInAPI(documentId, participants);
      setDocument(updatedDocument);
      try {
        setTaskQueue(await getDocumentTaskQueueFromAPI(documentId));
      } catch (_taskQueueError) {
        setTaskQueue(null);
      }
      setContractParticipantUpdateMessage('Identidades contractuales actualizadas');
    } catch (_error) {
      setError(workflowActionErrorMessage);
    } finally {
      setBusyAction(null);
    }
  };

  const handleTransportTransition = (targetStatus: DocumentLifecycleStatus) => {
    if (!documentId) return;

    runAction(
      'transport',
      () => transitionDocumentInAPI(documentId, targetStatus),
      (updatedDocument) => {
        setDocument(updatedDocument);
        setTransportUpdateMessage('Estado de transporte actualizado');
        getDocumentTaskQueueFromAPI(documentId)
          .then(setTaskQueue)
          .catch(() => setTaskQueue(null));
        getDocumentWorkflowActionsFromAPI(documentId)
          .then((actions) => {
            setWorkflowActions(actions);
            setWorkflowActionsUnavailable(false);
          })
          .catch(() => {
            setWorkflowActions(null);
            setWorkflowActionsUnavailable(true);
          });
      }
    );
  };

  const handleAuthorityReview = () => {
    if (!documentId) return;

    runAction(
      'authorityReview',
      () => recordAuthorityReviewInAPI(documentId, { outcome: 'reviewed' }),
      () => {
        setAuthorityReviewMessage('Revisión autoridad registrada');
        getDocumentWorkflowActionsFromAPI(documentId)
          .then((actions) => {
            setWorkflowActions(actions);
            setWorkflowActionsUnavailable(false);
          })
          .catch(() => {
            setWorkflowActions(null);
            setWorkflowActionsUnavailable(true);
          });
      }
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <p className="text-lg">Cargando flujo del documento...</p>
      </div>
    );
  }

  if (error && !document) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          {error}
        </div>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2">Procedimientos y firmas</h1>
          <p className="text-gray-700">
            {document?.title || documentId} · estado {document?.status || 'desconocido'}
          </p>
        </div>
        <Link
          to={`/view/${documentId}?source=api`}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-center"
        >
          Ver documento
        </Link>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-800 p-4 rounded mb-6">
        Flujo de base técnica parcial: esta pantalla registra aceptación de procedimientos y evidencia de firma simple
        vinculada a versiones inmutables del servidor. No constituye una declaración de cumplimiento legal/eIDAS.
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-6">
          {error}
        </div>
      )}

      {(contractParticipantIdentityRequired || entitledPartyAccessRequired) && (
        <section className="bg-white shadow-md rounded p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Identidades contractuales</h2>
          {entitledPartyAccessRequired && (
            <p className="mb-4 text-sm text-gray-700">
              El acceso backend de las partes eCMR identificadas esta pendiente de restablecimiento.
            </p>
          )}
          <form onSubmit={handleContractParticipantUpdate} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="workflowConsignorEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Email del remitente
              </label>
              <input
                id="workflowConsignorEmail"
                type="email"
                value={consignorEmail}
                onChange={(event) => setConsignorEmail(event.target.value)}
                autoComplete="email"
                required
                className="w-full rounded border px-3 py-2 text-gray-900"
              />
            </div>
            <div>
              <label htmlFor="workflowCarrierEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Email del transportista
              </label>
              <input
                id="workflowCarrierEmail"
                type="email"
                value={carrierEmail}
                onChange={(event) => setCarrierEmail(event.target.value)}
                autoComplete="email"
                required
                className="w-full rounded border px-3 py-2 text-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={busyAction === 'identities'}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              >
                {busyAction === 'identities' ? 'Guardando...' : 'Guardar identidades'}
              </button>
            </div>
          </form>
        </section>
      )}

      {contractParticipantUpdateMessage && (
        <p className="mb-6 border-l-4 border-green-500 bg-green-50 p-3 text-sm text-green-800">
          {contractParticipantUpdateMessage}
        </p>
      )}

      {lifecycleActions.length > 0 && (
        <section className="bg-cyan-50 border-l-4 border-cyan-500 text-cyan-900 shadow-md rounded p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">Acciones lifecycle server-authored</h2>
          <p className="text-sm mb-4">
            Acciones autorizadas por política backend sanitizada. Esta vista usa estados conocidos y no muestra endpoints, bloqueadores, roles, versiones ni metadatos crudos del backend.
          </p>
          <dl className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 text-sm">
            <div>
              <dt className="font-medium">Disponibles</dt>
              <dd>{countLabel(availableLifecycleActions.length, 'acción lifecycle disponible', 'acciones lifecycle disponibles')}</dd>
            </div>
            <div>
              <dt className="font-medium">Prerrequisitos</dt>
              <dd>{countLabel(prerequisiteBlockedLifecycleActions.length, 'acción bloqueada por prerrequisitos', 'acciones bloqueadas por prerrequisitos')}</dd>
            </div>
            <div>
              <dt className="font-medium">Autorización</dt>
              <dd>{countLabel(roleBlockedLifecycleActions.length, 'acción bloqueada por rol', 'acciones bloqueadas por rol')}</dd>
            </div>
            <div>
              <dt className="font-medium">Referencia backend</dt>
              <dd>
                {lifecycleActionsHaveBackendVersionReference
                  ? 'referencia backend disponible sin exponer identificadores crudos'
                  : 'sin referencia backend versionada disponible'}
              </dd>
            </div>
          </dl>
          <div className="flex flex-col gap-3 sm:flex-row">
            {availableLifecycleActions.map((action) => (
              action.targetStatus === 'delivered' ? (
                <Link
                  key={action.actionId}
                  to={`/evidence/${documentId}`}
                  className="bg-cyan-700 hover:bg-cyan-800 text-white font-bold py-2 px-4 rounded text-center"
                >
                  {workflowActionLabel(action.targetStatus)}
                </Link>
              ) : (
                <button
                  key={action.actionId}
                  type="button"
                  onClick={() => handleTransportTransition(action.targetStatus)}
                  disabled={busyAction === 'transport'}
                  className="bg-cyan-700 hover:bg-cyan-800 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                >
                  {workflowActionLabel(action.targetStatus)}
                </button>
              )
            ))}
          </div>
          {transportUpdateMessage && <p className="mt-3 text-sm font-medium">{transportUpdateMessage}</p>}
        </section>
      )}

      {authorityReviewActions.length > 0 && (
        <section className="bg-sky-50 border-l-4 border-sky-500 text-sky-900 shadow-md rounded p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">Revisión autoridad/delegado server-authored</h2>
          <p className="text-sm mb-4">
            Política backend de revisión documental sanitizada. Esta vista muestra solo conteos y enlaces conocidos; no expone roles, bloqueadores, endpoints, versiones ni metadatos crudos.
          </p>
          <dl className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 text-sm">
            <div>
              <dt className="font-medium">Disponibles</dt>
              <dd>{countLabel(availableAuthorityReviewActions.length, 'revisión disponible', 'revisiones disponibles')}</dd>
            </div>
            <div>
              <dt className="font-medium">Prerrequisitos</dt>
              <dd>{countLabel(prerequisiteBlockedAuthorityReviewActions.length, 'revisión bloqueada por prerrequisitos', 'revisiones bloqueadas por prerrequisitos')}</dd>
            </div>
            <div>
              <dt className="font-medium">Autorización</dt>
              <dd>{countLabel(roleBlockedAuthorityReviewActions.length, 'revisión bloqueada por rol', 'revisiones bloqueadas por rol')}</dd>
            </div>
            <div>
              <dt className="font-medium">Referencia backend</dt>
              <dd>
                {authorityReviewHasBackendVersionReference
                  ? 'referencia backend disponible sin exponer identificadores crudos'
                  : 'sin referencia backend versionada disponible'}
              </dd>
            </div>
          </dl>
          {availableAuthorityReviewActions.length > 0 && (
            <button
              type="button"
              onClick={handleAuthorityReview}
              disabled={busyAction === 'authorityReview'}
              className="inline-block bg-sky-700 hover:bg-sky-800 text-white font-bold py-2 px-4 rounded text-center disabled:opacity-50"
            >
              Registrar revisión autorizada
            </button>
          )}
          {authorityReviewMessage && <p className="mt-3 text-sm font-medium">{authorityReviewMessage}</p>}
        </section>
      )}

      {transportLifecycleTasks.length > 0 && (
        <section className="bg-cyan-50 border-l-4 border-cyan-500 text-cyan-900 shadow-md rounded p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">Flujo de transporte server-authored</h2>
          <p className="text-sm mb-4">
            Tareas activas desde política backend sanitizada. Esta vista usa rutas conocidas y no muestra etiquetas, brechas, roles, versiones ni enlaces crudos del backend.
          </p>
          <dl className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 text-sm">
            <div>
              <dt className="font-medium">Aceptación transportista</dt>
              <dd>{countLabel(carrierAcceptanceTaskCount, 'aceptación del transportista pendiente', 'aceptaciones del transportista pendientes')}</dd>
            </div>
            <div>
              <dt className="font-medium">Inicio tránsito</dt>
              <dd>{countLabel(transitStartTaskCount, 'inicio de tránsito pendiente', 'inicios de tránsito pendientes')}</dd>
            </div>
            <div>
              <dt className="font-medium">Entrega</dt>
              <dd>{countLabel(deliveryConfirmationTaskCount, 'confirmación de entrega pendiente', 'confirmaciones de entrega pendientes')}</dd>
            </div>
            <div>
              <dt className="font-medium">Referencia backend</dt>
              <dd>
                {transportLifecycleHasBackendVersionReference
                  ? 'referencia backend disponible sin exponer identificadores crudos'
                  : 'sin referencia backend versionada disponible'}
              </dd>
            </div>
          </dl>
          <div className="flex flex-col gap-3 sm:flex-row">
            {carrierAcceptanceTaskCount > 0 && (
              <button
                type="button"
                onClick={() => handleTransportTransition('accepted_by_carrier')}
                disabled={busyAction === 'transport'}
                className="bg-cyan-700 hover:bg-cyan-800 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              >
                Aceptar eCMR
              </button>
            )}
            {transitStartTaskCount > 0 && (
              <button
                type="button"
                onClick={() => handleTransportTransition('in_transit')}
                disabled={busyAction === 'transport'}
                className="bg-cyan-700 hover:bg-cyan-800 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              >
                Iniciar tránsito
              </button>
            )}
            {deliveryConfirmationTaskCount > 0 && (
              <Link
                to={`/evidence/${documentId}`}
                className="bg-cyan-700 hover:bg-cyan-800 text-white font-bold py-2 px-4 rounded text-center"
              >
                Abrir evidencia de entrega
              </Link>
            )}
          </div>
          {transportUpdateMessage && <p className="mt-3 text-sm font-medium">{transportUpdateMessage}</p>}
        </section>
      )}

      {referencedProcedureSets && (
        <section className="bg-white shadow-md rounded p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2">Procedimientos referenciados en la carta</h2>
          {referencedProcedureSets.unresolvedProcedureSetCount > 0 && (
            <p className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900 mb-4">
              {referencedProcedureSets.unresolvedProcedureSetCount}{' '}
              {referencedProcedureSets.unresolvedProcedureSetCount === 1
                ? 'referencia no resoluble'
                : 'referencias no resolubles'}
            </p>
          )}
          {referencedProcedureSets.procedureSets.length > 0 ? (
            referencedProcedureSets.procedureSets.map((referencedProcedureSet) => (
              <article
                key={`${referencedProcedureSet.procedureSetId}@${referencedProcedureSet.version}`}
                className="border-t border-gray-200 py-4 first:border-t-0 first:pt-0 last:pb-0"
              >
                <h3 className="font-semibold text-gray-900">
                  {referencedProcedureSet.title || 'Procedimientos eCMR registrados'}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  version {referencedProcedureSet.version} |{' '}
                  {referencedProcedureSet.active ? 'activa' : 'historica registrada'}
                  {referencedProcedureSet.digestAvailable ? ' | digest disponible' : ''}
                </p>
                {referencedProcedureSet.legalDisclaimer && (
                  <p className="text-sm text-gray-700 mb-3">{referencedProcedureSet.legalDisclaimer}</p>
                )}
                <dl className="space-y-3">
                  {Object.entries(referencedProcedureSet.procedures).map(([key, description]) => (
                    <div key={key}>
                      <dt className="font-medium text-gray-900">{key}</dt>
                      <dd className="text-gray-700">{description}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))
          ) : (
            <p className="text-sm text-gray-600">La carta no contiene referencias resolubles a procedimientos registrados.</p>
          )}
        </section>
      )}

      <section className="bg-white shadow-md rounded p-6 mb-6">
        <h2 className="text-xl font-semibold mb-2">Procedimientos activos para nuevas operaciones</h2>
        <p className="text-sm text-gray-600 mb-4">
          {procedureSet?.title || 'Conjunto activo'} · versión {procedureSet?.version || 'sin versión'} · {procedureSet?.digestAvailable ? 'digest disponible para validación backend' : 'digest no informado por backend'}
        </p>

        {procedureSet?.legalDisclaimer && (
          <p className="text-sm text-gray-700 mb-4">{procedureSet.legalDisclaimer}</p>
        )}

        <dl className="space-y-3 mb-4">
          {procedureEntries.map(([key, description]) => (
            <div key={key}>
              <dt className="font-medium text-gray-900">{key}</dt>
              <dd className="text-gray-700">{description}</dd>
            </div>
          ))}
        </dl>

        {acceptance ? (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-800 p-3 rounded">
            Procedimientos aceptados · referencia de evidencia backend disponible sin exponer identificadores crudos
            {acceptance.contractParticipantReferencePresent === true && (
              <> · vínculo con participante contractual vigente</>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={handleAcceptProcedures}
            disabled={!procedureSet || busyAction === 'accept'}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
          >
            {busyAction === 'accept' ? 'Aceptando...' : 'Aceptar procedimientos'}
          </button>
        )}
      </section>

      <section className="bg-white shadow-md rounded p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Firma server-side de versión inmutable</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="font-medium">Versión actual</p>
            <p>
              {currentVersion?.versionId || currentVersion?.versionNumber
                ? 'referencia de versión backend disponible sin exponer identificadores crudos'
                : 'Sin versión inmutable disponible'}
            </p>
          </div>
          <div>
            <p className="font-medium">Digest</p>
            <p>
              {currentVersion?.digestAvailable === true || currentVersion?.digest || currentVersion?.xmlContentDigest
                ? 'digest disponible para validación backend'
                : 'Sin digest disponible'}
            </p>
          </div>
        </div>

        {signature && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-800 p-3 rounded mb-4">
            Firma registrada para la versión inmutable · referencia de evidencia backend disponible sin exponer identificadores crudos
          </div>
        )}

        <button
          type="button"
          onClick={handleSignVersion}
          disabled={!currentVersion?.versionId || busyAction === 'sign'}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          {busyAction === 'sign' ? 'Firmando...' : 'Firmar versión actual'}
        </button>
      </section>

      <section className="bg-white shadow-md rounded p-6">
        <h2 className="text-xl font-semibold mb-4">Verificación de firmas</h2>
        {verification && (
          <div className={`border-l-4 p-3 rounded mb-4 ${
            verification.valid ? 'bg-green-100 border-green-500 text-green-800' : 'bg-red-100 border-red-500 text-red-800'
          }`}>
            <p>
              {verification.count} {verification.count === 1 ? 'firma verificada' : 'firmas verificadas'}
            </p>
            <p>
              {verification.integrity?.valid ? 'Integridad de versión válida' : 'Integridad de versión no validada'}
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={handleVerifySignatures}
          disabled={busyAction === 'verify'}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          {busyAction === 'verify' ? 'Verificando...' : 'Verificar firmas'}
        </button>
      </section>
    </div>
  );
};

export default DocumentWorkflow;
